import React, { useEffect, useState, useRef } from 'react';
import * as api from './api';
import { hasRealmRole, hasClientRole } from './api';
import { handleRedirect, login, logout, refreshIfNeeded, isAuthenticated, getUserInfo } from './auth';
import { DEMO_MODE, ON_CALLBACK } from './config';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import History from './pages/History';
import RatingsPage from './pages/RatingsPage';
import TemplatesAdmin from './pages/admin/Templates';
import ModelsAdmin from './pages/admin/Models';

export default function App() {
  const [page, setPage] = useState('dashboard');
  const [drawer, setDrawer] = useState(false);
  const [historyCount, setHistoryCount] = useState(null);

  const [authReady, setAuthReady] = useState(false);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (DEMO_MODE) return;
    if (!authReady) return;

    const ensureFresh = async () => {
      try {
        await refreshIfNeeded(true);
        if (!isAuthenticated()) {
          login();
        } else {
          setProfile(getUserInfo());
        }
      } catch {
        login();
      }
    };

    const onFocus = () => ensureFresh();
    const onVisible = () => { if (!document.hidden) ensureFresh(); };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [authReady]);

  useEffect(() => {
    if (DEMO_MODE) return;
    if (!authReady) return;
    (async () => {
      if (!isAuthenticated()) {
        const refreshed = await refreshIfNeeded(true);
        if (!refreshed && !isAuthenticated()) {
          login();
        } else {
          setProfile(getUserInfo());
        }
      }
    })();
  }, [authReady, page]);

  const didHandle = useRef(false);

  useEffect(() => {
    (async () => {
      if (DEMO_MODE) {
        setProfile(null);
        setAuthReady(true);
        return;
      }

      if (ON_CALLBACK) {
        if (didHandle.current) return;
        didHandle.current = true;

        await handleRedirect();
        setProfile(getUserInfo());
        setAuthReady(true);
        return;
      }

      try { await refreshIfNeeded(); } catch { }
      if (!isAuthenticated()) {
        login();
        return;
      }
      setProfile(getUserInfo());
      setAuthReady(true);
    })();
  }, []);

  useEffect(() => {
    if (DEMO_MODE) return;
    const i = setInterval(async () => {
      const changed = await refreshIfNeeded();
      if (changed) setProfile(getUserInfo());
    }, 30_000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    if (!authReady || (!isAuthenticated() && !DEMO_MODE)) return;
    (async () => {
      try {
        const h = await api.history();
        setHistoryCount(Array.isArray(h) ? h.length : 0);
      } catch (e) { console.error(e); }
    })();
  }, [authReady]);

  useEffect(() => {
    if (page !== 'history' || !authReady || (!isAuthenticated() && !DEMO_MODE)) return;
    api.history()
      .then(h => setHistoryCount(Array.isArray(h) ? h.length : 0))
      .catch(console.error);
  }, [page, authReady]);

  useEffect(() => {
    let obsDoc, obsShadow, intervalId;

    const removeBranding = () => {
      const host = document.querySelector('elevenlabs-convai');
      if (!host) return false;

      const sr = host.shadowRoot;
      if (!sr) return false;

      const p = [...sr.querySelectorAll('p')].find(
        el => /powered by elevenlabs/i.test(el.textContent || '')
      );

      const link = sr.querySelector('a[href*="elevenlabs.io"]');

      let removed = false;
      if (link && link.closest('p')) {
        link.closest('p').remove();
        removed = true;
      } else if (p) {
        p.remove();
        removed = true;
      }

      try {
        if ('adoptedStyleSheets' in sr) {
          const sheet = new CSSStyleSheet();
          sheet.replaceSync(`
          a[href*="elevenlabs.io"] { display:none !important; }
          p:where(:has(a[href*="elevenlabs.io"])) { display:none !important; }
        `);
          sr.adoptedStyleSheets = [...sr.adoptedStyleSheets, sheet];
        } else {
          const style = document.createElement('style');
          style.textContent = `
          a[href*="elevenlabs.io"] { display:none !important; }
        `;
          sr.appendChild(style);
        }
      } catch { }

      if (!obsShadow) {
        obsShadow = new MutationObserver(() => removeBranding());
        obsShadow.observe(sr, { childList: true, subtree: true });
      }

      return removed;
    };

    obsDoc = new MutationObserver(() => removeBranding());
    obsDoc.observe(document.documentElement, { childList: true, subtree: true });

    intervalId = setInterval(() => {
      if (removeBranding()) {
        clearInterval(intervalId);
        intervalId = null;
      }
    }, 300);

    removeBranding();

    return () => {
      obsDoc?.disconnect();
      obsShadow?.disconnect();
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  const [canManageModels, setCanManageModels] = useState(false);

  useEffect(() => {
    if (!authReady) return;
    const allowed =
      hasRealmRole('SuperVoice') ||
      hasClientRole(process.env.KEYCLOAK_CLIENT_ID || 'your-client-id', 'SuperVoice');
    setCanManageModels(!!allowed);
  }, [authReady, profile]);

  return (
    <div className="min-h-screen overflow-x-hidden">
      {(authReady && (isAuthenticated() || DEMO_MODE)) && (
        <Sidebar
          page={page}
          setPage={(p) => { setPage(p); setDrawer(false); }}
          historyCount={historyCount}
          isAuthed={isAuthenticated() || DEMO_MODE}
          profile={profile}
          onLogout={() => logout(true)}
          drawer={drawer}
          setDrawer={setDrawer}
          canManageModels={canManageModels}
        />
      )}

      <main className="p-6 md:p-8 space-y-6 md:pl-[var(--sbpad)] transition-[padding]">
        {/* Mobile topbar */}
        {authReady && (isAuthenticated() || DEMO_MODE) && (
          <div className="md:hidden sticky top-0 z-30 -mt-6 -mx-6 mb-2 px-4 py-3 bg-[#0f1523]/80 backdrop-blur border-b border-slate-800/60 flex items-center">
            <button
              className="btn btn-ghost"
              onClick={() => setDrawer(true)}
              aria-label="Отвори меню"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M3 12h18M3 18h18" />
              </svg>
            </button>
            <div className="flex-1" />
          </div>
        )}

        {authReady && (isAuthenticated() || DEMO_MODE) ? (
          <>
            {page === 'dashboard' && <Dashboard onAfterUpload={() => setHistoryCount(c => (c ?? 0) + 1)} />}
            {page === 'history' && <History setPage={setPage} />}
            {page === 'ratings' && <RatingsPage />}
            {page === 'templates' && <TemplatesAdmin onBack={() => setPage('dashboard')} />}
            {page === 'models' && canManageModels && <ModelsAdmin onBack={() => setPage('dashboard')} />}
            {page === 'models' && !canManageModels && (
              <div className="card rounded-2xl p-6 text-slate-300">Нямате достъп.</div>
            )}
            <elevenlabs-convai agent-id="agent_5301k0vqqpmcfd6a14w70he7dp7q"></elevenlabs-convai>
          </>
        ) : DEMO_MODE ? (
          <div className="card rounded-2xl p-6">
            <div className="text-slate-300">Демо режим.</div>
          </div>
        ) : (
          <div className="card rounded-2xl p-6">
            <div className="text-slate-300 mb-3">Пренасочване към екран за вход…</div>
          </div>
        )}
      </main>
    </div>
  );

}